import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, SoccerBall, MagnifyingGlass, User, Shield } from '@phosphor-icons/react';
import { createInitialGameState } from '../utils/gameData';
import { createPlayerCareerState } from '../utils/playerCareer';

export const PlayerCareerSelectPage = ({ onStartCareer }) => {
  const navigate = useNavigate();
  // Generate a fresh league world for the career (independent of league mode)
  const [gameWorld] = useState(() => createInitialGameState());
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [search, setSearch] = useState('');
  const [posFilter, setPosFilter] = useState('ALL');

  const teams = gameWorld.teams;

  const players = useMemo(() => {
    if (!selectedTeam) return [];
    return selectedTeam.players
      .filter(p => posFilter === 'ALL' || p.position === posFilter)
      .filter(p => !search.trim() || p.name.toLowerCase().includes(search.trim().toLowerCase()))
      .sort((a, b) => b.overall - a.overall);
  }, [selectedTeam, search, posFilter]);

  const handleConfirm = () => {
    if (!selectedTeam || !selectedPlayer) return;
    if (!window.confirm(`Start career as ${selectedPlayer.name} at ${selectedTeam.name}? You can restart later.`)) return;
    const ns = createPlayerCareerState(teams, selectedTeam.id, selectedPlayer.id);
    onStartCareer(ns);
    // Delay nav so parent can commit careerState before route guard re-evaluates
    setTimeout(() => navigate('/player-career'), 50);
  };

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button onClick={() => navigate('/')} variant="ghost" className="text-[#A0A0A5] hover:text-white" data-testid="career-back-btn"><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-wide flex items-center gap-3">
              <SoccerBall size={32} className="text-[#32D74B]" weight="fill" /> Choose Your Player
            </h1>
            <p className="text-[#A0A0A5] mt-1">Pick any team in the J.League, then pick your player to begin your career.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr_340px] gap-6">
          {/* Team column */}
          <Card className="bg-[#121216] border-[#2C2C35] p-4" data-testid="career-team-column">
            <h2 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
              <Shield size={16} className="text-[#00E5FF]" /> J.League Teams
            </h2>
            <ScrollArea className="h-[560px] pr-2">
              <div className="space-y-1.5">
                {teams.map(team => (
                  <button
                    key={team.id}
                    onClick={() => { setSelectedTeam(team); setSelectedPlayer(null); }}
                    data-testid={`career-team-${team.id}`}
                    className={`w-full text-left px-3 py-2 rounded-sm transition-all border ${selectedTeam?.id === team.id ? 'bg-[#C8102E]/15 border-[#C8102E]' : 'bg-[#0A0A0C] border-[#2C2C35] hover:border-[#6E6E73]'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-white font-medium truncate">{team.name}</span>
                      <span className="text-[10px] text-[#6E6E73] font-mono">{team.formation}</span>
                    </div>
                    <div className="text-[10px] text-[#6E6E73] mt-0.5">{team.city} • {team.players.length} players</div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </Card>

          {/* Player list column */}
          <Card className="bg-[#121216] border-[#2C2C35] p-4" data-testid="career-player-column">
            <h2 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
              <User size={16} className="text-[#FFD700]" /> {selectedTeam ? `${selectedTeam.name} Squad` : 'Pick a team to see players'}
            </h2>
            {selectedTeam && (
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6E6E73]" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search player..." className="pl-8 bg-[#0A0A0C] border-[#2C2C35] text-white text-sm h-9" data-testid="career-search-input" />
                </div>
                <Select value={posFilter} onValueChange={setPosFilter}>
                  <SelectTrigger className="w-[110px] bg-[#0A0A0C] border-[#2C2C35] text-white text-sm h-9" data-testid="career-pos-filter"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121216] border-[#2C2C35]">
                    {['ALL','GK','CB','LB','RB','CDM','CM','CAM','LW','RW','ST'].map(p => (
                      <SelectItem key={p} value={p} className="text-white focus:bg-[#C8102E]">{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <ScrollArea className="h-[500px] pr-2">
              <div className="space-y-1.5">
                {!selectedTeam && <p className="text-[#6E6E73] text-sm p-4 text-center">Select a team on the left to see its players.</p>}
                {players.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPlayer(p)}
                    data-testid={`career-player-${p.id}`}
                    className={`w-full text-left px-3 py-2 rounded-sm transition-all border flex items-center gap-3 ${selectedPlayer?.id === p.id ? 'bg-[#32D74B]/15 border-[#32D74B]' : 'bg-[#0A0A0C] border-[#2C2C35] hover:border-[#6E6E73]'}`}
                  >
                    <span className="text-xs font-mono text-[#6E6E73] w-6 text-right">#{p.number}</span>
                    <span className="text-base">{p.flag}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium truncate">{p.name}</p>
                      <p className="text-[10px] text-[#6E6E73]">{p.position} • Age {p.age} • Potential {p.potential}</p>
                    </div>
                    <span className={`font-mono font-bold text-base ${p.overall >= 85 ? 'text-[#FFD700]' : p.overall >= 75 ? 'text-white' : 'text-[#A0A0A5]'}`}>{p.overall}</span>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </Card>

          {/* Preview column */}
          <Card className="bg-[#121216] border-[#2C2C35] p-4" data-testid="career-preview-column">
            <h2 className="font-heading font-bold text-white uppercase text-sm mb-3">Your Career</h2>
            {!selectedPlayer ? (
              <div className="text-center py-12">
                <SoccerBall size={48} className="text-[#2C2C35] mx-auto mb-3" weight="fill" />
                <p className="text-[#6E6E73] text-sm">Pick a player to preview</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-[#0A0A0C] border border-[#32D74B]/40 rounded-sm p-4 text-center">
                  <div className="text-3xl mb-1">{selectedPlayer.flag}</div>
                  <p className="font-heading font-black text-xl text-white uppercase leading-tight">{selectedPlayer.name}</p>
                  <p className="text-xs text-[#A0A0A5] mt-1">{selectedPlayer.position} • {selectedPlayer.nationality}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-2 text-center">
                    <p className="text-[10px] text-[#6E6E73] uppercase">Overall</p>
                    <p className="font-mono font-black text-2xl text-[#FFD700]">{selectedPlayer.overall}</p>
                  </div>
                  <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-2 text-center">
                    <p className="text-[10px] text-[#6E6E73] uppercase">Potential</p>
                    <p className="font-mono font-black text-2xl text-[#32D74B]">{selectedPlayer.potential}</p>
                  </div>
                  <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-2 text-center">
                    <p className="text-[10px] text-[#6E6E73] uppercase">Age</p>
                    <p className="font-mono font-bold text-lg text-white">{selectedPlayer.age}</p>
                  </div>
                  <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-2 text-center">
                    <p className="text-[10px] text-[#6E6E73] uppercase">Squad #</p>
                    <p className="font-mono font-bold text-lg text-white">{selectedPlayer.number}</p>
                  </div>
                </div>
                <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-3">
                  <p className="text-[10px] text-[#6E6E73] uppercase mb-1">Team</p>
                  <p className="text-sm text-white font-medium">{selectedTeam?.name}</p>
                  <p className="text-[10px] text-[#6E6E73]">{selectedTeam?.city}</p>
                </div>
                <Button onClick={handleConfirm} className="w-full bg-[#32D74B] hover:bg-[#2CB53E] text-black font-heading font-bold uppercase tracking-wide" data-testid="career-start-btn">
                  Begin Career
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};
